import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  EmptyState,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ApproversTooltip } from 'components/Approvers/ApproversTooltip'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useApprovalsTransactionsQuery } from 'hooks/queries/useApprovalsTransactionsQuery'
import { ApprovalTableTypes } from 'pages/Approvals/types'
import { getProcessedByDetails, getTableHeaders } from 'pages/Approvals/util'
import { ApprovalActionStatuses, ApprovalStatuses } from 'types/approvals'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

export const ApprovedTransactions = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const currentAccountReference = currentAccount?.reference || ''
  const {
    data: approvedTransactions,
    isError,
    isFetching,
  } = useApprovalsTransactionsQuery(
    currentAccountReference,
    ApprovalStatuses.APPROVED,
    !!currentAccountReference
  )

  const {
    isSidePanelOpen: isApprovedTransactionDetailsOpen,
    sidePanelData: approvedTransactionDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(approvedTransactions)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return getTableHeaders(t, ApprovalTableTypes.APPROVED)
  }, [t])

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!approvedTransactions) return []

    return approvedTransactions?.map((approvalTransaction, index) => {
      const { isProcessedByMultiple, isProcessedBySingle, processedByText } = getProcessedByDetails(
        approvalTransaction?.processedBy,
        ApprovalActionStatuses.PAYOUT_MERCHANT_APPROVAL_APPROVED,
        t
      )

      return {
        id: approvalTransaction?.correlationKey,
        testid: approvalTransaction?.correlationKey,
        onRowClick: () => {
          openSidePanelSummary(index)
        },
        cells: [
          {
            key: 'payment',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar size="small" name={approvalTransaction?.currency?.code} />,
            Text: `${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`,
            BottomText: approvalTransaction?.walletName,
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text:
              approvalTransaction?.beneficiaryDetails?.accountNumber ||
              approvalTransaction?.beneficiaryDetails?.destinationWallet,
            BottomText: approvalTransaction?.beneficiaryDetails?.beneficiaryName,
          },
          {
            key: 'approvedBy',
            variant: DataTableCellVariants.TEXT,
            ...(isProcessedBySingle && {
              Text:
                approvalTransaction?.processedBy[0]?.userFullName ??
                approvalTransaction?.processedBy[0]?.email,
              BottomText: dayjs(approvalTransaction?.actionedAt).format(
                DateFormat.D_MMM_YYYY_AT_HH_MM
              ),
            }),
            ...(isProcessedByMultiple && {
              Text: (
                <ApproversTooltip
                  processedBy={approvalTransaction?.processedBy}
                  status={ApprovalActionStatuses.PAYOUT_MERCHANT_APPROVAL_APPROVED}
                >
                  {processedByText}
                </ApproversTooltip>
              ),
            }),
            ...(!isProcessedBySingle &&
              !isProcessedByMultiple && {
                Text: approvalTransaction?.actionedBy,
                BottomText: dayjs(approvalTransaction?.actionedAt).format(
                  DateFormat.D_MMM_YYYY_AT_HH_MM
                ),
              }),
          },
        ],
      }
    })
  }, [approvedTransactions])

  return (
    <Box paddingB={16}>
      <>
        {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

        {!isFetching && isError && (
          <EmptyState
            testId="error-state"
            LottiePlayer={<Lottie src="connection-error" />}
            title={t('oopsSomethingWentWrong')}
          />
        )}

        {!isFetching && !isError && !approvedTransactions?.length && (
          <EmptyState
            testId="empty-state"
            LottiePlayer={<Lottie src="no-data" />}
            title={t('approvals.emptyStateHeadline', {
              approvalStatus: ApprovalStatuses.APPROVED.toLowerCase(),
            })}
            description={t('approvals.emptyStateDescription', {
              approvalStatus: ApprovalStatuses.APPROVED.toLowerCase(),
            })}
          />
        )}

        {!isFetching && !isError && !!approvedTransactions?.length && (
          <>
            <DataTable name="approved-transactions-table" headers={columns} data={tableData} />
            {approvedTransactionDetails && (
              <SidePanelSummary
                isOpen={isApprovedTransactionDetailsOpen}
                paging={paging}
                onClose={() => setIsSidePanelOpen(false)}
                transactionDetails={approvedTransactionDetails}
                tab={ApprovalStatuses.APPROVED}
              />
            )}
          </>
        )}
      </>
    </Box>
  )
}
